import { Box } from '@components/Box';
import { Button } from '@components/Button';
import {
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  useEffect,
} from 'react';
import { FaSpinner } from 'react-icons/fa6';
import { IoCheckmark, IoClose, IoPaperPlane } from 'react-icons/io5';
import type { FileWithPreview } from './AttachementsList';
import AttachmentsList from './AttachementsList';
import toast from 'react-hot-toast';
import { Text } from '@components/Text';
import { cn } from '@utils/cn';

const ALLOWED_FILE_TYPES = [
  'text/plain',
  'application/pdf',
  'application/json',
  'text/markdown',
  'image/jpeg',
  'image/png',
  'image/gif',
];

const DESCRIPTION_MIN_LENGTH = 20;

interface ChatBoxProps {
  onSubmit: (description: string, files: FileWithPreview[]) => Promise<boolean>;
  onSuccess?: () => void;
  onError?: (error: Error) => void;

  onDescriptionChange?: (description: string) => void;
  onFilesChange?: (files: FileWithPreview[]) => void;
  maxFileSize?: number; // Size in bytes

  showCTALabel?: boolean;
}

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const ChatBox: React.FC<ChatBoxProps> = ({
  onSubmit,
  onSuccess,
  onError,
  onDescriptionChange,
  onFilesChange,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  showCTALabel = true,
}) => {
  const [agentDescription, setAgentDescription] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<FileWithPreview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup object URLs when files are removed
  useEffect(() => {
    return () => {
      for (const file of attachedFiles) {
        if (file.preview) URL.revokeObjectURL(file.preview);
      }
    };
  }, [attachedFiles]);

  useEffect(() => {
    if (isLoading || !textareaRef.current) return;

    textareaRef.current.focus();
  }, [isLoading]);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(Math.max(40, textarea.scrollHeight), 200);
      textarea.style.height = `${newHeight}px`;
    }
  };

  const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setAgentDescription(e.target.value);
    adjustTextareaHeight();
    if (typeof onDescriptionChange === 'function') {
      onDescriptionChange(e.target.value);
    }
  };

  const validateFile = (file: File): boolean => {
    if (file.size > maxFileSize) {
      const maxSizeMB = maxFileSize / (1024 * 1024);
      toast.error(
        `File ${file.name} is too large. Maximum size is ${maxSizeMB}MB`,
      );
      return false;
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      toast.error(`File type ${file.type} is not supported`);
      return false;
    }
    return true;
  };

  const createFilePreview = (file: FileWithPreview) => {
    if (file.type.startsWith('image/')) {
      file.preview = URL.createObjectURL(file);
    }
    return file;
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files)
        .filter(validateFile)
        .map((file) => createFilePreview(file as FileWithPreview));

      setAttachedFiles((prev) => {
        if (typeof onFilesChange === 'function') {
          onFilesChange([...prev, ...newFiles]);
        }
        return [...prev, ...newFiles];
      });
    }
    // Reset input value to allow uploading the same file again
    e.target.value = '';
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => {
      const file = prev[index];
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
      const newFiles = prev.filter((_, i) => i !== index);
      if (typeof onFilesChange === 'function') {
        onFilesChange(newFiles);
      }
      return newFiles;
    });
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreateAgentClick();
    }
  };

  const handleCreateAgentClick = async () => {
    if (isLoading || !isDescriptionValid) return;

    try {
      setIsLoading(true);
      const description = agentDescription;

      // Clear user description prior to submittion
      // since its an asynchronous call...

      await onSubmit(description, attachedFiles);

      if (typeof onSuccess === 'function') {
        onSuccess();
      }

      setAttachedFiles((prev) => {
        for (const file of prev) {
          if (file.preview) URL.revokeObjectURL(file.preview);
        }
        return [];
      });
    } catch (err) {
      if (typeof onError === 'function') {
        onError(err as Error);
      }
      toast.error(
        err instanceof Error ? err.message : 'Failed to create agent',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const trimmedDescription = agentDescription.trim();
  const isDescriptionValid =
    trimmedDescription.length >= DESCRIPTION_MIN_LENGTH;

  return (
    <Box className="flex flex-col gap-4 max-w-[700px] self-center w-full">
      <Box
        data-testid="chat-input-container"
        className="gap-2 sm:gap-4 m-auto w-full flex-shrink-0 min-h-[5rem] max-h-[calc(100vh-8rem)] relative flex flex-col p-6 sm:p-8 rounded-[8px] bg-elz-neutral-100 border border-elz-neutral-6 bg-elz-neutral-2"
      >
        <AttachmentsList
          files={attachedFiles}
          onRemoveFile={handleRemoveFile}
        />

        <textarea
          ref={textareaRef}
          className="min-h-[6rem] sm:min-h-[8rem] w-full bg-transparent resize-none border-none outline-none overflow-y-auto placeholder:text-elz-neutral-8 text-elz-neutral-12 text-sm sm:text-base"
          placeholder={`What do you want your AI agent to do? Write at least ${DESCRIPTION_MIN_LENGTH} characters.`}
          value={agentDescription}
          onChange={handleMessageChange}
          onKeyDown={handleKeyPress}
          disabled={isLoading}
        />

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          className="hidden"
          multiple
          accept={ALLOWED_FILE_TYPES.join(',')}
        />

        <Box className="w-full flex flex-row justify-between items-center gap-12">
          {/*<Button
            variant="ghost-bordered"
            onClick={handleUploadClick}
            disabled={isLoading}
            className="text-sm sm:text-base"
          >
            <IoAttach className="text-lg" />
            Upload {attachedFiles.length > 0 && `(${attachedFiles.length})`}
          </Button>*/}
          <Box
            className={cn(
              'gap-6 items-center flex-row text-elz-neutral-11',
              isDescriptionValid && 'text-elz-success-10',
            )}
          >
            {isDescriptionValid ? (
              <IoCheckmark className="size-16" />
            ) : (
              <IoClose className="size-16" />
            )}
            <Text
              variant="secondary"
              size="xs"
              className={cn(isDescriptionValid && 'text-elz-success-10')}
            >
              Minimum {DESCRIPTION_MIN_LENGTH} characters
            </Text>
          </Box>
          <Button
            variant="accent"
            disabled={!isDescriptionValid || isLoading}
            onClick={handleCreateAgentClick}
            className="text-sm sm:text-base"
          >
            {isLoading ? (
              <FaSpinner className="size-16 animate-spin" />
            ) : (
              <IoPaperPlane className="size-16" />
            )}
            {showCTALabel && 'Create agent'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
